import { useEffect, useRef, useState } from "react";
import { es } from "date-fns/locale";
import { Calendar } from "./ui/calendar";
import { CalendarDays, Clock } from "@/components/icons";

/**
 * Glassmorphism datetime picker (12-hour format).
 *
 * value: string "YYYY-MM-DDTHH:mm" (24h internally, displayed in 12h)
 * onChange(newValue: string)
 * busyRanges: array of {start: Date, end: Date} — slots overlapping these ranges are disabled
 */
export default function DateTimePicker({
  value,
  onChange,
  testid = "datetime-picker",
  placeholder = "Selecciona fecha y hora",
  busyRanges = [],
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const parsed = parseValue(value);
  // Derive day/hour/minute directly from props.
  const day = parsed.day;
  const hour = parsed.hour;
  const minute = parsed.minute;
  // Internal-only state for calendar month navigation.
  const [visibleMonth, setVisibleMonth] = useState(day || new Date());

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isSlotBusy = (h, m) => {
    if (!day) return false;
    const slot = new Date(
      day.getFullYear(),
      day.getMonth(),
      day.getDate(),
      h,
      m
    );
    return busyRanges.some(
      (r) => slot.getTime() >= r.start.getTime() && slot.getTime() < r.end.getTime()
    );
  };

  // For an hour, count how many of the 12 minute-buckets are busy
  const hourFullyBusy = (h) =>
    [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].every((m) =>
      isSlotBusy(h, m)
    );

  const commit = (nextDay, nextHour, nextMinute) => {
    if (!nextDay) return;
    const y = nextDay.getFullYear();
    const m = String(nextDay.getMonth() + 1).padStart(2, "0");
    const d = String(nextDay.getDate()).padStart(2, "0");
    const hh = String(nextHour).padStart(2, "0");
    const mm = String(nextMinute).padStart(2, "0");
    onChange(`${y}-${m}-${d}T${hh}:${mm}`);
  };

  const onPickDay = (d) => {
    if (!d) return;
    // If no time set yet, default to the current value or 09:00.
    commit(d, hour ?? 9, minute ?? 0);
    setVisibleMonth(d);
  };

  // 12h conversion helpers
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = ((hour + 11) % 12) + 1; // 0->12, 1->1, ..., 12->12, 13->1, ..., 23->11

  const setFromParts = (h12, period, mNew) => {
    let h24;
    if (period === "AM") h24 = h12 === 12 ? 0 : h12;
    else h24 = h12 === 12 ? 12 : h12 + 12;
    if (day) commit(day, h24, mNew);
  };

  const onChangeHour12 = (h12) => setFromParts(h12, ampm, minute);
  const onChangeAmPm = (p) => setFromParts(hour12, p, minute);
  const onChangeMinute = (m) => setFromParts(hour12, ampm, m);

  // Display for trigger
  const display = day
    ? `${day.toLocaleDateString("es-ES", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })} · ${hour12}:${String(minute).padStart(2, "0")} ${ampm}`
    : placeholder;

  // Keyboard-typeable text representation: "dd/mm/yyyy hh:mm" (24h)
  const typedValue = day
    ? `${String(day.getDate()).padStart(2, "0")}/${String(day.getMonth() + 1).padStart(2, "0")}/${day.getFullYear()} ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
    : "";
  const [draft, setDraft] = useState(null);
  const typed = draft !== null ? draft : typedValue;

  const parseTyped = (raw) => {
    // dd/mm/yyyy hh:mm  (hh 0-23)
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})[\s,T]+(\d{1,2}):(\d{2})$/.exec(
      raw.trim()
    );
    if (!m) return false;
    let dd = +m[1], mm = +m[2], yy = +m[3], hh = +m[4], mi = +m[5];
    if (yy < 100) yy = yy > 50 ? 1900 + yy : 2000 + yy;
    if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || hh > 23 || mi > 59) return false;
    const testDate = new Date(yy, mm - 1, dd);
    if (
      testDate.getFullYear() !== yy ||
      testDate.getMonth() !== mm - 1 ||
      testDate.getDate() !== dd
    )
      return false;
    commit(testDate, hh, mi);
    setVisibleMonth(testDate);
    setDraft(null);
    return true;
  };

  // Build hour options 1..12 in current period; disable hours fully busy
  const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1);

  // Minute options for currently selected hour
  const minuteOptions = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <input
          type="text"
          value={typed}
          placeholder={placeholder + " — ej. 25/12/2026 14:30"}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={(e) => {
            const v = e.target.value;
            if (!v.trim()) {
              setDraft(null);
              return;
            }
            if (!parseTyped(v)) {
              setDraft(null); // revert on bad input
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              parseTyped(e.target.value);
              e.currentTarget.blur();
            }
          }}
          data-testid={`${testid}-input`}
          className="w-full glass-input rounded-xl pl-4 pr-11 py-3 text-sm"
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={`${testid}-trigger`}
          title="Abrir calendario"
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg hover:bg-black/5 text-black/60 flex items-center justify-center transition"
        >
          <CalendarDays className="w-4 h-4" />
        </button>
      </div>
      {day && (
        <div className="mt-1.5 text-[11px] text-black/60 capitalize">{display}</div>
      )}

      {open && (
        <>
          {/* Backdrop blocks clicks under the picker */}
          <div
            className="fixed inset-0 z-40 bg-black/15 backdrop-blur-[2px]"
            onClick={() => setOpen(false)}
            data-testid={`${testid}-backdrop`}
          />
          <div
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-[28px] border border-black/[0.06] shadow-[0_22px_60px_-14px_rgba(0,0,0,0.25)] p-5 sm:p-6 w-[640px] max-w-[calc(100vw-2rem)] max-h-[90vh] overflow-auto"
            data-testid={`${testid}-popover`}
          >
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-4">
            <div>
              <Calendar
                mode="single"
                locale={es}
                selected={day || undefined}
                month={visibleMonth}
                onMonthChange={(m) => setVisibleMonth(m)}
                onSelect={onPickDay}
                className="rounded-xl bg-white p-3"
                classNames={{
                  caption_label: "text-sm font-extrabold text-[#163144] capitalize",
                  head_cell:
                    "text-[#1B405B]/60 font-bold w-9 h-8 text-[10px] uppercase tracking-wider flex items-center justify-center",
                  day:
                    "h-9 w-9 p-0 mx-auto font-medium rounded-full hover:bg-black/[0.05] !text-[#163144] flex items-center justify-center transition cursor-pointer",
                  day_selected:
                    "!bg-[#01BC8E] !text-white !font-bold hover:!bg-[#01BC8E] focus:!bg-[#01BC8E]",
                  day_today:
                    "ring-2 ring-[#1B405B] ring-offset-1 font-bold !text-[#1B405B]",
                  day_outside: "!text-[#1B405B]/30 hover:bg-transparent",
                  day_disabled: "!text-[#1B405B]/30 opacity-50",
                }}
              />
            </div>

            <div className="sm:border-l sm:border-black/10 sm:pl-4 flex flex-col">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#1B405B]/65 flex items-center gap-1 mb-3">
                <Clock className="w-3 h-3" /> Hora
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <select
                    value={hour12}
                    onChange={(e) => onChangeHour12(parseInt(e.target.value))}
                    className="flex-1 bg-white border border-black/[0.06] rounded-xl px-3 py-2.5 text-sm font-bold text-[#163144]"
                    data-testid={`${testid}-hour`}
                  >
                    {hourOptions.map((h) => {
                      const h24 =
                        ampm === "AM"
                          ? h === 12
                            ? 0
                            : h
                          : h === 12
                          ? 12
                          : h + 12;
                      const disabled = hourFullyBusy(h24);
                      return (
                        <option key={h} value={h} disabled={disabled}>
                          {String(h).padStart(2, "0")}
                          {disabled ? " · ocupada" : ""}
                        </option>
                      );
                    })}
                  </select>

                  <span className="text-xl font-extrabold text-[#1B405B]/55">
                    :
                  </span>

                  <select
                    value={minute}
                    onChange={(e) => onChangeMinute(parseInt(e.target.value))}
                    className="flex-1 bg-white border border-black/[0.06] rounded-xl px-3 py-2.5 text-sm font-bold text-[#163144]"
                    data-testid={`${testid}-minute`}
                  >
                    {minuteOptions.map((m) => {
                      const disabled = isSlotBusy(hour, m);
                      return (
                        <option key={m} value={m} disabled={disabled}>
                          {String(m).padStart(2, "0")}
                          {disabled ? " · ocupado" : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="inline-flex w-full rounded-full bg-black/[0.05] p-0.5 text-xs font-bold">
                  {["AM", "PM"].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => onChangeAmPm(p)}
                      className={`flex-1 px-3 py-1.5 rounded-full transition ${
                        ampm === p
                          ? "bg-white text-black shadow-sm"
                          : "text-black/55 hover:text-black"
                      }`}
                      data-testid={`${testid}-${p.toLowerCase()}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {isSlotBusy(hour, minute) && (
                <div className="mt-3 text-xs font-semibold text-red-600 leading-snug">
                  Esta hora ya tiene una cita. Elige otro horario.
                </div>
              )}

              <div className="mt-auto pt-4 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const now = new Date();
                    const m5 = Math.round(now.getMinutes() / 5) * 5;
                    commit(now, now.getHours(), m5);
                    setVisibleMonth(now);
                  }}
                  className="px-3 py-2 rounded-full text-xs font-bold text-black bg-white border border-black/[0.06] hover:bg-black/[0.04] transition"
                >
                  Ahora
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  data-testid={`${testid}-confirm`}
                  className="px-4 py-2.5 rounded-full text-sm font-bold bg-[#01BC8E] hover:bg-[#01BC8E] text-white transition disabled:opacity-60"
                  disabled={isSlotBusy(hour, minute)}
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
          </div>
        </>
      )}
    </div>
  );
}

function parseValue(v) {
  if (!v || typeof v !== "string") return { day: null, hour: 9, minute: 0 };
  const match = v.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return { day: null, hour: 9, minute: 0 };
  const [, y, mo, d, h, mi] = match;
  return {
    day: new Date(parseInt(y), parseInt(mo) - 1, parseInt(d)),
    hour: parseInt(h),
    minute: parseInt(mi),
  };
}
